﻿import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { dashboardDataAtom } from "../state/dashboardData";
import { filtersAtom } from "../state/filter";
import * as XLSX from "xlsx";
import ManualSaleForm from "./ManualSaleForm";
import BulkUploadModal from "./BulkUploadModal";
import { useAuth } from "./AuthContext";
import { useNavigate } from "react-router-dom";
import { Wrench, Search, X, Filter } from "lucide-react";
import { showError, showSuccess } from "../utils/showToast";
import LiveIndicator from "./LiveIndicator";
import { sharedTableStyles } from "./TableStyles";
import SaleDetailModal from "./SaleDetailModal";
import { API_BASE_URL } from "../config/api";

// ═══════════════════════════════════════════════════════
// ✅ CACHE MANAGER (Singleton - persists across renders)
// ═══════════════════════════════════════════════════════
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const CACHE_KEY_PREFIX = "sales_cache_";

interface CacheEntry {
    data: any[];
    timestamp: number;
}

const cacheManager = {
    get(key: string): any[] | null {
        try {
            const cached = sessionStorage.getItem(CACHE_KEY_PREFIX + key);
            if (!cached) return null;
            const entry: CacheEntry = JSON.parse(cached);
            if (Date.now() - entry.timestamp > CACHE_TTL) {
                sessionStorage.removeItem(CACHE_KEY_PREFIX + key);
                return null;
            }
            return entry.data;
        } catch {
            return null;
        }
    },
    set(key: string, data: any[]) {
        try {
            const entry: CacheEntry = { data, timestamp: Date.now() };
            sessionStorage.setItem(CACHE_KEY_PREFIX + key, JSON.stringify(entry));
        } catch (e) {
            console.warn("Cache storage full, clearing old entries");
            Object.keys(sessionStorage)
                .filter((k) => k.startsWith(CACHE_KEY_PREFIX))
                .forEach((k) => sessionStorage.removeItem(k));
        }
    },
    invalidate(key: string) {
        sessionStorage.removeItem(CACHE_KEY_PREFIX + key);
    },
};

// ═══════════════════════════════════════════════════════
// ✅ REQUEST DEDUPLICATION (Prevent duplicate API calls)
// ═══════════════════════════════════════════════════════
const pendingRequests = new Map<string, Promise<any>>();

// ═══════════════════════════════════════════════════════
// Inline Confirm Modal
// ═══════════════════════════════════════════════════════
interface ConfirmModalProps {
    isOpen: boolean;
    message: string;
    onConfirm: () => void;
    onCancel: () => void;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({ isOpen, message, onConfirm, onCancel }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onCancel} />
            <div className="relative z-10 bg-[#111111] border border-[#2d2d2d] rounded-lg shadow-xl px-8 py-7 flex flex-col items-center gap-6 min-w-[300px] max-w-sm w-full mx-4">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#1a1a1a] border border-[#2d2d2d]">
                    <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                        />
                    </svg>
                </div>
                <p className="text-sm text-gray-300 text-center leading-relaxed">{message}</p>
                <div className="flex items-center gap-3 w-full">
                    <button
                        onClick={onCancel}
                        className="flex-1 px-4 py-2 text-sm font-medium bg-[#1a1a1a] border border-[#2d2d2d] text-gray-400 hover:bg-[#222222] hover:text-white rounded transition-colors"
                    >
                        No
                    </button>
                    <button
                        onClick={onConfirm}
                        className="flex-1 px-4 py-2 text-sm font-medium bg-red-600/20 border border-red-500/40 text-red-400 hover:bg-red-600/30 hover:text-red-300 rounded transition-colors"
                    >
                        Yes
                    </button>
                </div>
            </div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════
// ✅ SKELETON LOADER
// ═══════════════════════════════════════════════════════
const TableSkeleton: React.FC = () => (
    <div className="p-4 space-y-2 animate-pulse">
        {[...Array(8)].map((_, i) => (
            <div key={i} className="flex gap-4">
                <div className="h-10 bg-[#1a1a1a] rounded flex-1" />
                <div className="h-10 bg-[#1a1a1a] rounded w-24" />
                <div className="h-10 bg-[#1a1a1a] rounded w-24" />
                <div className="h-10 bg-[#1a1a1a] rounded w-32" />
                <div className="h-10 bg-[#1a1a1a] rounded w-20" />
            </div>
        ))}
    </div>
);

// ═══════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════
function normalizeDate(d: Date) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function formatStatus(status?: string): string {
    if (!status) return "—";
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

function getStatusBadgeClasses(status?: string, isReversal?: boolean): string {
    const base = "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium";

    if (isReversal && status === "ReturnedForExchange") {
        return `${base} bg-purple-500/10 text-purple-400 border border-purple-500/20`;
    }

    if (isReversal) {
        return `${base} bg-orange-500/10 text-orange-400 border border-orange-500/20`;
    }

    switch (status?.toLowerCase()) {
        case "created":
            return `${base} bg-green-500/10 text-green-400 border border-green-500/20`;
        case "exchanged":
            return `${base} bg-blue-500/10 text-blue-400 border border-blue-500/20`;
        case "cancelled":
        case "unsold":
            return `${base} bg-red-500/10 text-red-400 border border-red-500/20`;
        case "returned":
            return `${base} bg-orange-500/10 text-orange-400 border border-orange-500/20`;
        default:
            return `${base} bg-gray-500/10 text-gray-400 border border-gray-500/20`;
    }
}

function isCancellationReversal(item: any): boolean {
    const carePrice = parseFloat(item?.carePrice ?? 0);

    if (!(carePrice < 0 && item?.sold === false)) return false;

    if (item?.status === "ReturnedForExchange") return false;

    return item?.status === "created" || item?.status === "cancelled";
}

function isExchangeReturn(item: any): boolean {
    return item?.status === "ReturnedForExchange";
}

function isHiddenTransaction(item: any): boolean {
    const productPrice = parseFloat(item?.productPrice ?? "0");
    const carePrice = parseFloat(item?.carePrice ?? "0");
    const isProductNegative = productPrice < 0;
    const isCarePriceZero = carePrice === 0;
    const hasNoRoot = item?.rootUniqueId === null || item?.rootUniqueId === undefined || item?.rootUniqueId === "";
    const isUnsold = item?.sold === false;

    return isProductNegative && isCarePriceZero && hasNoRoot && isUnsold;
}

// ✅ UPDATED: Supports both Care+ and Non-Care+ Replacements
function isReplacementItem(item: any, allItems: any[]): boolean {
    if (!item) return false;
    // Exclude refund log rows and cancelled rows from being flagged as replacement badges
    if (["ReturnedForExchange", "cancelled"].includes(item?.status)) return false;

    const itemRoot = item?.rootUniqueId || item?.uniqueId;
    const itemEventId = item?.eventId;

    // Find all inventory/sales items belonging to the same exchange chain
    const chainItems = allItems.filter((other: any) => {
        if (["ReturnedForExchange", "cancelled"].includes(other?.status)) return false;

        // Match by rootUniqueId or uniqueId link
        const otherRoot = other?.rootUniqueId || other?.uniqueId;
        if (itemRoot && otherRoot && itemRoot === otherRoot) {
            return true;
        }

        // Match by eventId if present
        if (itemEventId && other?.eventId && itemEventId === other.eventId) {
            return true;
        }

        // Cross match rootUniqueId to uniqueId
        if (
            item?.rootUniqueId &&
            (other?.uniqueId === item.rootUniqueId || other?.rootUniqueId === item.rootUniqueId)
        ) {
            return true;
        }
        if (
            other?.rootUniqueId &&
            (item?.uniqueId === other.rootUniqueId || item?.rootUniqueId === other.rootUniqueId)
        ) {
            return true;
        }

        return false;
    });

    if (chainItems.length <= 1) {
        return false; // Standalone purchase, no exchange chain
    }

    // Sort chain items chronologically to determine their order in the chain
    chainItems.sort((a: any, b: any) => {
        const dateA = new Date(a.purchaseDate || a.createdAt || a.date || 0).getTime();
        const dateB = new Date(b.purchaseDate || b.createdAt || b.date || 0).getTime();
        if (dateA !== dateB) return dateA - dateB;
        return String(a.id || "").localeCompare(String(b.id || ""));
    });

    // Find position in chain
    const itemIndex = chainItems.findIndex(
        (ci: any) => (ci.id && ci.id === item.id) || (ci.uniqueId && ci.uniqueId === item.uniqueId),
    );

    // Index 0 is the original purchase. Index > 0 are replacements!
    return itemIndex > 0;
}

const buildColumns = (onCancel: (id: number) => void, allItems: any[]): TableColumn<any>[] => [
    {
        name: "Invoice ID",
        selector: (r) => r.uniqueId,
        sortable: true,
        wrap: true,
        minWidth: "120px",
        cell: (r) => <span className="font-mono text-sm">{r.uniqueId}</span>,
    },
    {
        name: "Care+ ID",
        selector: (r) => r.id,
        sortable: true,
        minWidth: "110px",
        cell: (r) => <span className="font-mono text-sm">{r.id}</span>,
    },
    {
        name: "Care+ Price",
        selector: (r) => r.carePrice,
        sortable: true,
        minWidth: "110px",
        cell: (r) => {
            if (r.carePrice == null || r.carePrice === "") return <span className="text-gray-600">—</span>;
            const price = Number(r.carePrice);
            if (price < 0)
                return <span className="text-orange-400 font-medium">-₹{Math.abs(price).toLocaleString("en-IN")}</span>;
            return `₹${price.toLocaleString("en-IN")}`;
        },
    },
    { name: "Product Name", selector: (r) => r.productName, sortable: true, wrap: true, minWidth: "140px" },
    {
        name: "Product Price",
        selector: (r) => r.productPrice,
        sortable: true,
        minWidth: "120px",
        cell: (r) => {
            if (r.productPrice == null || r.productPrice === "") return <span className="text-gray-600">—</span>;
            const price = Number(r.productPrice);
            if (price < 0)
                return <span className="text-orange-400 font-medium">-₹{Math.abs(price).toLocaleString("en-IN")}</span>;
            return `₹${price.toLocaleString("en-IN")}`;
        },
    },
    {
        name: "Status",
        selector: (r) => r.status,
        sortable: true,
        minWidth: "120px",
        cell: (r) => {
            const isReversal = isCancellationReversal(r);
            const isExchReturn = isExchangeReturn(r);

            if (isReversal || isExchReturn) {
                return <span className={getStatusBadgeClasses(r.status, true)}>Refund</span>;
            }

            return <span className={getStatusBadgeClasses(r.status, false)}>{formatStatus(r.status)}</span>;
        },
    },
    {
        name: "Purchase Date",
        selector: (r) => r.transactionDate,
        sortable: true,
        minWidth: "120px",
        cell: (r) => {
            if (!r.transactionDate) return <span className="text-gray-600">—</span>;

            // Convert the API's UTC string into a local Date object
            const d = new Date(r.transactionDate);

            // Fallback just in case the date is invalid
            if (isNaN(d.getTime())) {
                return <span className="text-gray-400 text-sm">{r.transactionDate.slice(0, 10)}</span>;
            }

            // Extract Local Year, Month, and Day to format as YYYY-MM-DD
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");

            return <span className="text-gray-400 text-sm">{`${year}-${month}-${day}`}</span>;
        },
    },
    {
        name: "OPT IN",
        minWidth: "150px",
        cell: (r) => {
            if (isExchangeReturn(r)) {
                return (
                    <span className="text-xs text-purple-400 font-medium" title="Item returned for exchange">
                        Returned For Exchange
                    </span>
                );
            }

            if (isCancellationReversal(r)) {
                return (
                    <span className="text-xs text-orange-400 font-medium" title="Cancellation refund adjustment">
                        Refund
                    </span>
                );
            }

            if (r.status === "cancelled") {
                return <span className="text-xs text-gray-400 font-medium">Cancelled</span>;
            }

            // ✅ CHECK REPLACEMENT BEFORE !r.sold TO COVER ALL REPLACEMENTS
            if (isReplacementItem(r, allItems)) {
                return (
                    <span
                        className="text-xs text-blue-400 font-medium"
                        title={r.rootUniqueId ? `Replacement for ${r.rootUniqueId}` : "Replacement"}
                    >
                        Replacement
                    </span>
                );
            }

            // Fallback for non-care+ items
            if (!r.sold) {
                return <span className="text-xs text-red-400 font-medium">False</span>;
            }

            // Standard Care+ items
            return (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onCancel(r.id);
                    }}
                    className="text-xs text-green-400 font-medium hover:underline"
                >
                    True
                </button>
            );
        },
    },
];

// ═══════════════════════════════════════════════════════
// ✅ MAIN COMPONENT
// ═══════════════════════════════════════════════════════
export const RecentTransactions: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const data = useRecoilValue(dashboardDataAtom);
    const setDashboardData = useSetRecoilState(dashboardDataAtom);
    const filters = useRecoilValue(filtersAtom);

    const abortControllerRef = useRef<AbortController | null>(null);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const isMountedRef = useRef(true);

    const [isLoading, setIsLoading] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [showManual, setShowManual] = useState(false);
    const [showBulk, setShowBulk] = useState(false);
    const [selectedSale, setSelectedSale] = useState<any | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL");

    const [confirmModal, setConfirmModal] = useState<{
        isOpen: boolean;
        saleId: number | null;
    }>({ isOpen: false, saleId: null });

    const isAdmin = user?.userName === "omkark@securedstitch.com";

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            abortControllerRef.current?.abort();
        };
    }, []);

    const fetchData = useCallback(
        async (forceRefresh = false) => {
            const memberUuid = isAdmin
                ? (filters.member as string) || localStorage.getItem("memberUuid")
                : localStorage.getItem("memberUuid");

            const cacheKey = isAdmin ? `admin_${filters.member || "all"}` : `member_${memberUuid}`;

            if (!forceRefresh) {
                const cached = cacheManager.get(cacheKey);
                if (cached) {
                    console.log("⚡ Loaded from cache (instant!)");
                    setDashboardData(cached);
                    setIsInitialLoad(false);
                    setTimeout(() => fetchData(true), 100);
                    return;
                }
            }

            if (pendingRequests.has(cacheKey)) {
                console.log("🔄 Request already in progress, waiting...");
                try {
                    const result = await pendingRequests.get(cacheKey);
                    if (isMountedRef.current && Array.isArray(result)) {
                        setDashboardData(result);
                    }
                } catch {}
                return;
            }

            abortControllerRef.current?.abort();
            abortControllerRef.current = new AbortController();

            const timeoutId = setTimeout(() => abortControllerRef.current?.abort(), 60000);

            setIsLoading(true);

            const fetchPromise = (async () => {
                try {
                    let response;
                    if (isAdmin) {
                        response = await fetch(`${API_BASE_URL}/api/sale`, {
                            signal: abortControllerRef.current!.signal,
                        });
                    } else {
                        if (!memberUuid) return null;
                        response = await fetch(`${API_BASE_URL}/api/crd/sales`, {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ memberUuid }),
                            signal: abortControllerRef.current!.signal,
                        });
                    }

                    if (!response.ok) throw new Error("Fetch failed");
                    const result = await response.json();

                    if (!Array.isArray(result)) return null;

                    let items = result;
                    if (isAdmin && filters.member) {
                        items = items.filter((item: any) => item.memberUuid === filters.member);
                    }

                    cacheManager.set(cacheKey, items);
                    return items;
                } finally {
                    clearTimeout(timeoutId);
                    pendingRequests.delete(cacheKey);
                }
            })();

            pendingRequests.set(cacheKey, fetchPromise);

            try {
                const items = await fetchPromise;
                if (isMountedRef.current && items) {
                    setDashboardData(items);
                }
            } catch (err: any) {
                if (err.name === "AbortError") {
                    console.log("Request cancelled");
                } else {
                    if (isMountedRef.current) showError("Failed to fetch data.");
                }
            } finally {
                if (isMountedRef.current) {
                    setIsLoading(false);
                    setIsInitialLoad(false);
                }
            }
        },
        [isAdmin, filters.member, setDashboardData],
    );

    useEffect(() => {
        fetchData();
        intervalRef.current = setInterval(() => fetchData(true), 900000);
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [filters.member]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCancel = useCallback(
        (id: number) => {
            const memberUuid = isAdmin
                ? (filters.member as string) || localStorage.getItem("memberUuid")
                : localStorage.getItem("memberUuid");
            if (!memberUuid) return;
            setConfirmModal({ isOpen: true, saleId: id });
        },
        [isAdmin, filters.member],
    );

    const executeCancel = async () => {
        const id = confirmModal.saleId;
        setConfirmModal({ isOpen: false, saleId: null });
        if (!id) return;

        const memberUuid = isAdmin
            ? (filters.member as string) || localStorage.getItem("memberUuid")
            : localStorage.getItem("memberUuid");
        if (!memberUuid) return;

        try {
            const res = await fetch(`${API_BASE_URL}/api/sale/${id}`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ memberUuid }),
            });
            if (!res.ok) {
                let msg = "Failed to cancel.";
                try {
                    const d = await res.json();
                    if (d?.error) msg = d.error;
                } catch {}
                showError(msg);
                return;
            }
            const cacheKey = isAdmin ? `admin_${filters.member || "all"}` : `member_${memberUuid}`;
            cacheManager.invalidate(cacheKey);
            await fetchData(true);
            setSelectedSale((prev: any) => (prev?.id === id ? { ...prev, status: "cancelled" } : prev));
            showSuccess("Order cancelled.");
        } catch {
            showError("Failed to cancel.");
        }
    };

    const tableData = useMemo(() => {
        let items = data;

        items = items.filter((item: any) => !isHiddenTransaction(item));

        if (filters.fromDate && filters.toDate) {
            const from = normalizeDate(filters.fromDate);
            const to = normalizeDate(filters.toDate);
            items = items.filter((item: any) => {
                if (!item.transactionDate) return false;
                const d = normalizeDate(new Date(item.transactionDate));
                return d >= from && d <= to;
            });
        }
        return items;
    }, [data, filters.fromDate, filters.toDate]);

    const sortedData = useMemo(
        () =>
            [...tableData].sort((a, b) => {
                const dateA = new Date(a.transactionDate || a.createdAt || 0).getTime();
                const dateB = new Date(b.transactionDate || b.createdAt || 0).getTime();

                // Primary sort: Latest transactionDate first
                if (dateA !== dateB) {
                    return dateB - dateA;
                }

                // Secondary tie-breaker sort: Newest Care+ ID first if dates match
                return (b.id || 0) - (a.id || 0);
            }),
        [tableData],
    );

    const filteredData = useMemo(() => {
        let list = sortedData;

        if (statusFilter !== "ALL") {
            list = list.filter((item) => {
                const itemStatus = String(item?.status ?? "").toLowerCase();
                const targetFilter = statusFilter.toLowerCase();

                if (targetFilter === "returnedforexchange") {
                    return item?.status === "ReturnedForExchange" || isCancellationReversal(item);
                }

                return itemStatus === targetFilter;
            });
        }

        const q = debouncedSearch.trim().toLowerCase();
        if (!q) return list;

        const qDigits = q.replace(/\D/g, "");

        const scored = list
            .map((item) => {
                const invoiceId = String(item.uniqueId ?? "").toLowerCase();
                const carePlusId = String(item.id ?? "").toLowerCase();
                const customerName = String(item.customerName ?? "").toLowerCase();
                const phone = String(item.phoneNo ?? item.phone ?? "").toLowerCase();
                const phoneDigits = phone.replace(/\D/g, "");

                let score = -1;

                if (invoiceId === q) score = 1;
                else if (invoiceId.startsWith(q)) score = 2;
                else if (invoiceId.includes(q)) score = 3;
                else if (carePlusId === q) score = 4;
                else if (carePlusId.startsWith(q)) score = 5;
                else if (carePlusId.includes(q)) score = 6;
                else if (customerName.length > 0 && customerName.startsWith(q)) score = 7;
                else if (customerName.length > 0 && customerName.includes(q)) score = 8;
                else if (qDigits.length >= 3 && phoneDigits.includes(qDigits)) score = 9;
                else if (phone.length > 0 && phone.includes(q)) score = 10;

                return { item, score };
            })
            .filter((r) => r.score !== -1)
            .sort((a, b) => a.score - b.score);

        return scored.map((r) => r.item);
    }, [sortedData, debouncedSearch, statusFilter]);

    const columns = useMemo(() => buildColumns(handleCancel, data ?? []), [handleCancel, data]);

    const exportToExcel = useCallback(() => {
        const ws = XLSX.utils.json_to_sheet(filteredData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Transactions");
        XLSX.writeFile(wb, `transactions_${new Date().toISOString().split("T")[0]}.xlsx`);
    }, [filteredData]);

    const handleManualRefresh = useCallback(() => {
        const memberUuid = isAdmin
            ? (filters.member as string) || localStorage.getItem("memberUuid")
            : localStorage.getItem("memberUuid");
        const cacheKey = isAdmin ? `admin_${filters.member || "all"}` : `member_${memberUuid}`;
        cacheManager.invalidate(cacheKey);
        fetchData(true);
    }, [isAdmin, filters.member, fetchData]);

    return (
        <div className="bg-[#111111] border border-[#2d2d2d] rounded-none overflow-hidden">
            <div className="px-5 py-3.5 border-b border-[#2d2d2d] space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <h2 className="text-sm font-semibold text-white">Recent Transactions</h2>
                    <div className="flex flex-wrap items-center gap-2">
                        {isAdmin && (
                            <button
                                onClick={() => navigate("/service-dashboard")}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-[#1a1a1a] border border-[#2d2d2d] text-gray-300 hover:bg-[#222222] hover:text-white rounded transition-colors"
                            >
                                <Wrench className="w-3.5 h-3.5" />
                                <span className="hidden sm:inline">Service Dashboard</span>
                                <span className="sm:hidden">Service</span>
                            </button>
                        )}
                        <button
                            onClick={() => setShowManual(true)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-[#1a1a1a] border border-[#2d2d2d] text-gray-300 hover:bg-[#222222] hover:text-white rounded transition-colors"
                        >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            <span className="hidden sm:inline">Create Care+</span>
                            <span className="sm:hidden">New</span>
                        </button>
                        <button
                            onClick={() => setShowBulk(true)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-[#1a1a1a] border border-[#2d2d2d] text-gray-300 hover:bg-[#222222] hover:text-white rounded transition-colors"
                        >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                />
                            </svg>
                            <span className="hidden sm:inline">Bulk Care+</span>
                            <span className="sm:hidden">Upload</span>
                        </button>
                        <button
                            onClick={exportToExcel}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-[#1a1a1a] border border-[#2d2d2d] text-gray-300 hover:bg-[#222222] hover:text-white rounded transition-colors"
                        >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                />
                            </svg>
                            Export
                        </button>
                        <LiveIndicator isLoading={isLoading} onRefresh={handleManualRefresh} />
                    </div>
                </div>

                {/* ═══════════════════════════════════════════════════════
                    ⭐ WIDER SEARCH BOX (Fits full text without trimming)
                       & STATUS FILTER DROPDOWN
                   ═══════════════════════════════════════════════════════ */}
                <div className="flex flex-wrap items-center gap-3">
                    {/* Increased Width to sm:w-[460px] to show full text comfortably */}
                    <div className="relative w-full sm:w-[460px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search by Invoice ID, Care+ ID, Name, or Phone..."
                            className="w-full pl-9 pr-9 py-2 text-sm bg-[#1a1a1a] border border-[#2d2d2d] text-gray-200 placeholder:text-gray-500 rounded focus:outline-none focus:border-[#404040] focus:ring-1 focus:ring-[#404040]"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery("")}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 p-0.5"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>

                    {/* Status Filter Dropdown Chip (Sits right next to the search box) */}
                    <div className="relative w-full sm:w-48">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none flex items-center gap-1">
                            <Filter className="w-4 h-4" />
                        </div>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="w-full pl-9 pr-8 py-2 text-sm bg-[#1a1a1a] border border-[#2d2d2d] text-gray-200 rounded focus:outline-none focus:border-[#404040] focus:ring-1 focus:ring-[#404040] cursor-pointer appearance-none font-medium"
                        >
                            <option value="ALL">All Statuses</option>
                            <option value="created">Created</option>
                            <option value="unsold">Unsold</option>
                            <option value="exchanged">Exchanged</option>
                            <option value="ReturnedForExchange">Returned / Refund</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>
                    </div>
                </div>
            </div>

            {isInitialLoad && isLoading ? (
                <TableSkeleton />
            ) : (
                <DataTable
                    columns={columns}
                    data={filteredData}
                    onRowClicked={(row) => setSelectedSale(row)}
                    pagination
                    paginationPerPage={10}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    responsive
                    customStyles={sharedTableStyles}
                    progressPending={isLoading && !isInitialLoad}
                    progressComponent={
                        <div className="flex items-center justify-center gap-2 py-10 text-gray-500 text-sm">
                            <div className="w-4 h-4 border-2 border-gray-600 border-t-gray-300 rounded-full animate-spin" />
                            Refreshing…
                        </div>
                    }
                    noDataComponent={
                        <div className="py-10 text-center text-gray-600 text-sm">
                            {debouncedSearch || statusFilter !== "ALL"
                                ? `No transactions match your filters. Try resetting search or status.`
                                : "No transactions in the selected period."}
                        </div>
                    }
                    highlightOnHover
                    pointerOnHover
                />
            )}

            <div className="px-5 py-3 border-t border-[#2d2d2d] flex justify-between items-center">
                {statusFilter !== "ALL" ? (
                    <button
                        onClick={() => setStatusFilter("ALL")}
                        className="text-xs text-blue-400 hover:underline flex items-center gap-1"
                    >
                        <span>Reset Status Filter</span>
                    </button>
                ) : (
                    <span />
                )}
                <span className="text-xs text-gray-600">
                    {filteredData.length} {debouncedSearch || statusFilter !== "ALL" ? `of ${sortedData.length}` : ""}{" "}
                    records
                </span>
            </div>

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                message="Do you want to cancel this transaction?"
                onConfirm={executeCancel}
                onCancel={() => setConfirmModal({ isOpen: false, saleId: null })}
            />

            <SaleDetailModal sale={selectedSale} allSales={data ?? []} onClose={() => setSelectedSale(null)} />

            <ManualSaleForm
                isOpen={showManual}
                onClose={() => setShowManual(false)}
                onSuccess={() => fetchData(true)}
                memberUuid={isAdmin && filters.member ? (filters.member as string) : undefined}
            />

            <BulkUploadModal
                isOpen={showBulk}
                onClose={() => setShowBulk(false)}
                onSuccess={() => fetchData(true)}
                memberUuid={isAdmin && filters.member ? (filters.member as string) : undefined}
            />
        </div>
    );
};
